import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { internationalAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, MapPin, Calendar, CalendarDays, Users, Clock, Loader2, Save,
  Pencil, Trash2, ExternalLink, Copy, Send, IndianRupee, ShoppingBag,
  ChevronRight, Globe, Building, Phone, Mail, MessageSquare, UserPlus,
  BarChart3, FileText, Link,
} from 'lucide-react';

const EVENT_STATUS_STYLES = {
  draft: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280', label: 'Draft' },
  upcoming: { bg: 'rgba(59,130,246,0.08)', text: '#2563EB', label: 'Upcoming' },
  active: { bg: 'rgba(16,185,129,0.08)', text: '#059669', label: 'Active' },
  completed: { bg: 'rgba(107,114,128,0.08)', text: '#6B7280', label: 'Completed' },
  cancelled: { bg: 'rgba(239,68,68,0.08)', text: '#DC2626', label: 'Cancelled' },
};

export default function PopUpEventsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [viewingReport, setViewingReport] = useState(null);
  const [inviteData, setInviteData] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await internationalAPI.listEvents();
      setEvents(res.data || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async (data) => {
    setSaving(true);
    try {
      await internationalAPI.createEvent(data);
      toast.success('Event created');
      setCreating(false);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateEvent = async (data) => {
    setSaving(true);
    try {
      await internationalAPI.updateEvent(editingEvent.id, data);
      toast.success('Event updated');
      setEditingEvent(null);
      fetchEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event? This cannot be undone.')) return;
    try {
      await internationalAPI.deleteEvent(id);
      toast.success('Event deleted');
      fetchEvents();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleInviteClients = async (eventId) => {
    try {
      const res = await internationalAPI.inviteClients(eventId);
      setInviteData(res.data);
    } catch (err) {
      toast.error('Failed to generate invitations');
    }
  };

  const handleViewReport = async (eventId) => {
    try {
      const res = await internationalAPI.getEventReport(eventId);
      setViewingReport(res.data);
    } catch {
      toast.error('Failed to load report');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-12 w-48" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-48" />)}
        </div>
      </div>
    );
  }

  const upcomingEvents = events.filter(e => e.status === 'upcoming' || e.status === 'active');
  const pastEvents = events.filter(e => e.status === 'completed' || e.status === 'cancelled');

  return (
    <div className="p-6 space-y-6" data-testid="popup-events-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>Pop-Up Events</h1>
          <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>
            Manage international pop-up events, appointments, and client campaigns
          </p>
        </div>
        <Button className="gap-2 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
          onClick={() => setCreating(true)} data-testid="create-event-btn">
          <Plus className="w-4 h-4" /> New Event
        </Button>
      </div>

      {/* Active / Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>Active & Upcoming</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingEvents.map(event => (
              <EventCard key={event.id} event={event}
                onEdit={() => setEditingEvent(event)}
                onDelete={() => handleDelete(event.id)}
                onInvite={() => handleInviteClients(event.id)}
                onViewReport={() => handleViewReport(event.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Past Events */}
      {pastEvents.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold" style={{ color: 'var(--ink-muted)' }}>Past Events</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pastEvents.map(event => (
              <EventCard key={event.id} event={event} isPast
                onEdit={() => setEditingEvent(event)}
                onDelete={() => handleDelete(event.id)}
                onViewReport={() => handleViewReport(event.id)} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {events.length === 0 && (
        <Card className="rounded-xl border border-dashed border-border/60 bg-white">
          <CardContent className="py-12 text-center">
            <Globe className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--ink-muted)' }} />
            <p className="text-sm font-medium mb-1" style={{ color: 'var(--navy-ink)' }}>No Pop-Up Events</p>
            <p className="text-xs mb-4" style={{ color: 'var(--ink-muted)' }}>
              Create events for Dubai, Delhi, or other cities to manage international consultations.
            </p>
            <Button className="gap-2 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={() => setCreating(true)}>
              <Plus className="w-4 h-4" /> Create First Event
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Event Sheet */}
      <EventFormSheet
        open={creating || !!editingEvent}
        onOpenChange={(open) => { if (!open) { setCreating(false); setEditingEvent(null); } }}
        event={editingEvent}
        onSave={editingEvent ? handleUpdateEvent : handleCreateEvent}
        saving={saving}
      />

      {/* Invite Clients Dialog */}
      <InviteClientsDialog
        open={!!inviteData}
        onOpenChange={(open) => { if (!open) setInviteData(null); }}
        data={inviteData}
        onCopy={copyToClipboard}
      />

      {/* Event Report Dialog */}
      <EventReportDialog
        open={!!viewingReport}
        onOpenChange={(open) => { if (!open) setViewingReport(null); }}
        data={viewingReport}
      />
    </div>
  );
}

/* ============================================================
   Event Card Component
   ============================================================ */
function EventCard({ event, isPast, onEdit, onDelete, onInvite, onViewReport }) {
  const status = EVENT_STATUS_STYLES[event.status] || EVENT_STATUS_STYLES.draft;
  const bookingUrl = `${window.location.origin}/book/${event.slug}`;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm hover:shadow-md transition-shadow"
      data-testid={`event-card-${event.id}`}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Badge className="text-[9px] px-1.5 py-0" style={{ backgroundColor: status.bg, color: status.text }}>
                {status.label}
              </Badge>
              {event.is_active && (
                <Badge variant="outline" className="text-[9px] px-1 py-0" style={{ borderColor: '#059669', color: '#059669' }}>
                  Bookable
                </Badge>
              )}
            </div>
            <h3 className="text-sm font-bold truncate" style={{ color: 'var(--brand-navy)' }}>{event.name}</h3>
          </div>
        </div>

        {/* Location */}
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#DC2626' }} />
          <p className="text-xs truncate" style={{ color: 'var(--navy-ink)' }}>
            {event.venue_name}, {event.city}, {event.country}
          </p>
        </div>

        {/* Dates */}
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#2563EB' }} />
          <p className="text-xs" style={{ color: 'var(--navy-ink)' }}>
            {new Date(event.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
            {event.start_date !== event.end_date && ` – ${new Date(event.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
          </p>
          <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
            {event.daily_start_time} - {event.daily_end_time}
          </span>
        </div>

        {/* Stats (if available) */}
        {event.stats && (
          <div className="grid grid-cols-3 gap-2 mb-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
            <div className="text-center">
              <p className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>{event.stats.appointments_booked || 0}</p>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Bookings</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold" style={{ color: 'var(--brand-navy)' }}>{event.stats.orders_taken || 0}</p>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Orders</p>
            </div>
            <div className="text-center">
              <p className="text-sm font-bold" style={{ color: '#059669' }}>₹{((event.stats.total_revenue || 0) / 1000).toFixed(0)}k</p>
              <p className="text-[9px]" style={{ color: 'var(--ink-muted)' }}>Revenue</p>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-2 flex-wrap">
          {!isPast && onInvite && (
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 flex-1"
              onClick={onInvite} data-testid={`invite-btn-${event.id}`}>
              <Send className="w-3 h-3" /> Invite Clients
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1 flex-1"
            onClick={onViewReport} data-testid={`report-btn-${event.id}`}>
            <BarChart3 className="w-3 h-3" /> Report
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onEdit}>
            <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onDelete}>
            <Trash2 className="w-3.5 h-3.5" style={{ color: '#DC2626' }} />
          </Button>
        </div>

        {/* Booking Link */}
        {event.is_active && !isPast && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
            <div className="flex items-center gap-2">
              <Link className="w-3 h-3 flex-shrink-0" style={{ color: 'var(--ink-muted)' }} />
              <input readOnly value={bookingUrl} className="flex-1 text-[10px] bg-transparent border-none p-0 outline-none"
                style={{ color: 'var(--ink-muted)' }} />
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success('Booking link copied'); }}>
                <Copy className="w-3 h-3" style={{ color: '#2563EB' }} />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================================================
   Event Form Sheet
   ============================================================ */
function EventFormSheet({ open, onOpenChange, event, onSave, saving }) {
  const [form, setForm] = useState({
    name: '', city: '', country: 'UAE', venue_name: '', venue_address: '',
    start_date: '', end_date: '', daily_start_time: '10:00', daily_end_time: '19:00',
    slot_duration_minutes: 60, max_per_slot: 2, notes: '', is_active: true,
  });

  useEffect(() => {
    if (event) {
      setForm({
        name: event.name || '',
        city: event.city || '',
        country: event.country || 'UAE',
        venue_name: event.venue_name || '',
        venue_address: event.venue_address || '',
        start_date: event.start_date || '',
        end_date: event.end_date || '',
        daily_start_time: event.daily_start_time || '10:00',
        daily_end_time: event.daily_end_time || '19:00',
        slot_duration_minutes: event.slot_duration_minutes || 60,
        max_per_slot: event.max_per_slot || 2,
        notes: event.notes || '',
        is_active: event.is_active !== false,
      });
    } else {
      setForm({
        name: '', city: '', country: 'UAE', venue_name: '', venue_address: '',
        start_date: '', end_date: '', daily_start_time: '10:00', daily_end_time: '19:00',
        slot_duration_minutes: 60, max_per_slot: 2, notes: '', is_active: true,
      });
    }
  }, [event, open]);

  const handleSubmit = () => {
    if (!form.name || !form.city || !form.venue_name || !form.start_date) {
      toast.error('Please fill required fields');
      return;
    }
    onSave(form);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-[600px] sm:w-[640px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle style={{ color: 'var(--brand-navy)' }}>
            {event ? 'Edit Pop-Up Event' : 'Create Pop-Up Event'}
          </SheetTitle>
          <SheetDescription>
            {event ? 'Update event details' : 'Set up a new international pop-up event'}
          </SheetDescription>
        </SheetHeader>
        
        <div className="space-y-4 mt-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-xs font-medium">Event Name *</Label>
              <Input value={form.name} className="h-9 mt-1" placeholder="Dubai Summer 2026"
                onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                data-testid="event-name-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">City *</Label>
              <Input value={form.city} className="h-9 mt-1" placeholder="Dubai"
                onChange={e => setForm(prev => ({ ...prev, city: e.target.value }))}
                data-testid="event-city-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">Country *</Label>
              <Select value={form.country} onValueChange={v => setForm(prev => ({ ...prev, country: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="event-country-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UAE">UAE</SelectItem>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                  <SelectItem value="USA">United States</SelectItem>
                  <SelectItem value="Singapore">Singapore</SelectItem>
                  <SelectItem value="Hong Kong">Hong Kong</SelectItem>
                  <SelectItem value="India">India</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Venue */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium">Venue Name *</Label>
              <Input value={form.venue_name} className="h-9 mt-1" placeholder="The Ritz-Carlton"
                onChange={e => setForm(prev => ({ ...prev, venue_name: e.target.value }))}
                data-testid="event-venue-input" />
            </div>
            <div>
              <Label className="text-xs font-medium">Venue Address</Label>
              <Input value={form.venue_address} className="h-9 mt-1" placeholder="DIFC, Dubai"
                onChange={e => setForm(prev => ({ ...prev, venue_address: e.target.value }))} />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-medium">Start Date *</Label>
              <Input type="date" value={form.start_date} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, start_date: e.target.value, end_date: prev.end_date || e.target.value }))}
                data-testid="event-start-date" />
            </div>
            <div>
              <Label className="text-xs font-medium">End Date *</Label>
              <Input type="date" value={form.end_date} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, end_date: e.target.value }))}
                data-testid="event-end-date" />
            </div>
          </div>

          {/* Time & Slots */}
          <div className="grid grid-cols-4 gap-4">
            <div>
              <Label className="text-xs font-medium">Start Time</Label>
              <Input type="time" value={form.daily_start_time} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, daily_start_time: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs font-medium">End Time</Label>
              <Input type="time" value={form.daily_end_time} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, daily_end_time: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs font-medium">Slot Duration (min)</Label>
              <Input type="number" value={form.slot_duration_minutes} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, slot_duration_minutes: parseInt(e.target.value) || 60 }))} />
            </div>
            <div>
              <Label className="text-xs font-medium">Max per Slot</Label>
              <Input type="number" value={form.max_per_slot} className="h-9 mt-1"
                onChange={e => setForm(prev => ({ ...prev, max_per_slot: parseInt(e.target.value) || 2 }))} />
            </div>
          </div>

          {/* Notes */}
          <div>
            <Label className="text-xs font-medium">Notes</Label>
            <Textarea value={form.notes} rows={3} className="mt-1 text-xs" placeholder="Internal notes about the event..."
              onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Active for Bookings</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                Allow clients to book appointments for this event
              </p>
            </div>
            <Switch checked={form.is_active} onCheckedChange={v => setForm(prev => ({ ...prev, is_active: v }))}
              data-testid="event-active-toggle" />
          </div>
        </div>

        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
            data-testid="save-event-btn">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {event ? 'Update Event' : 'Create Event'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

/* ============================================================
   Invite Clients Dialog
   ============================================================ */
function InviteClientsDialog({ open, onOpenChange, data, onCopy }) {
  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--brand-navy)' }}>
            Invite Clients to {data.event?.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Stats */}
          <div className="flex items-center gap-4 p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" style={{ color: '#2563EB' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>
                {data.total_clients} clients in {data.event?.city}
              </span>
            </div>
            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1" onClick={() => onCopy(data.booking_link)}>
              <Copy className="w-3 h-3" /> Copy Booking Link
            </Button>
          </div>

          {/* Message Template */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>WhatsApp Message Template</Label>
            <div className="relative mt-1">
              <Textarea value={data.message_template} rows={8} readOnly className="text-xs pr-10" />
              <Button variant="ghost" size="sm" className="absolute top-2 right-2 h-7 w-7 p-0"
                onClick={() => onCopy(data.message_template)}>
                <Copy className="w-3.5 h-3.5" style={{ color: '#2563EB' }} />
              </Button>
            </div>
          </div>

          {/* Client List Preview */}
          <div>
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>
              Clients to Invite ({data.clients?.length || 0})
            </Label>
            <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
              {data.clients?.map(client => (
                <div key={client.id} className="flex items-center justify-between px-3 py-2 border-b last:border-b-0"
                  style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                  <span className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                    {client.first_name} {client.last_name}
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{client.phone}</span>
                </div>
              ))}
              {data.clients?.length === 0 && (
                <p className="text-xs text-center py-6" style={{ color: 'var(--ink-muted)' }}>
                  No clients found in this location
                </p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   Event Report Dialog
   ============================================================ */
function EventReportDialog({ open, onOpenChange, data }) {
  if (!data) return null;

  const { event, summary } = data;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle style={{ color: 'var(--brand-navy)' }}>
            Event Report: {event?.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Event Info */}
          <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--ink-muted)' }}>
            <div className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {event?.venue_name}, {event?.city}
            </div>
            <div className="flex items-center gap-1">
              <CalendarDays className="w-3.5 h-3.5" />
              {event?.start_date} – {event?.end_date}
            </div>
          </div>

          {/* Summary Stats */}
          <div className="grid grid-cols-4 gap-3">
            {[
              { label: 'Appointments', value: summary?.total_appointments, icon: CalendarDays, color: '#2563EB' },
              { label: 'Orders', value: summary?.total_orders, icon: ShoppingBag, color: '#059669' },
              { label: 'Revenue', value: `₹${((summary?.total_revenue || 0) / 1000).toFixed(0)}k`, icon: IndianRupee, color: '#059669' },
              { label: 'Avg Order', value: `₹${((summary?.average_order_value || 0) / 1000).toFixed(0)}k`, icon: BarChart3, color: '#7C3AED' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div key={label} className="p-3 rounded-lg text-center" style={{ backgroundColor: color + '08' }}>
                <Icon className="w-5 h-5 mx-auto mb-1" style={{ color }} />
                <p className="text-lg font-bold" style={{ color: 'var(--brand-navy)' }}>{value}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{label}</p>
              </div>
            ))}
          </div>

          {/* Client Breakdown */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
              <p className="text-lg font-bold" style={{ color: 'var(--brand-navy)' }}>{summary?.unique_clients || 0}</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Total Clients</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(16,185,129,0.05)' }}>
              <p className="text-lg font-bold" style={{ color: '#059669' }}>{summary?.new_clients || 0}</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>New Clients</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(59,130,246,0.05)' }}>
              <p className="text-lg font-bold" style={{ color: '#2563EB' }}>{summary?.returning_clients || 0}</p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Returning</p>
            </div>
          </div>

          {/* Appointment Stats */}
          <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
            <p className="text-xs font-medium mb-2" style={{ color: 'var(--navy-ink)' }}>Appointment Summary</p>
            <div className="grid grid-cols-3 gap-3 text-center text-xs">
              <div>
                <p className="font-bold" style={{ color: 'var(--brand-navy)' }}>{summary?.total_appointments || 0}</p>
                <p style={{ color: 'var(--ink-muted)' }}>Total</p>
              </div>
              <div>
                <p className="font-bold" style={{ color: '#059669' }}>{summary?.completed_appointments || 0}</p>
                <p style={{ color: 'var(--ink-muted)' }}>Completed</p>
              </div>
              <div>
                <p className="font-bold" style={{ color: '#DC2626' }}>{summary?.cancelled_appointments || 0}</p>
                <p style={{ color: 'var(--ink-muted)' }}>Cancelled</p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
